import React, { useState, useEffect, useMemo } from 'react'
import {
  Activity, Server, Database, Cpu, HardDrive, RefreshCw,
  Search, ShieldAlert, CheckCircle, Clock, Filter, AlertTriangle,
  Layers, Terminal, BarChart2, Globe, ArrowUpRight, Copy, Check,
  Zap, Shield, HelpCircle
} from 'lucide-react'
import {
  getClients, getPulseFleet, getPulseAnomalies, getPulseStatus,
  getFleetCoverage, getAgentLogs
} from '../api'

export default function TelemetryExplorer({ clients = [] }) {
  const [clientList, setClientList] = useState(clients)
  const [selectedClient, setSelectedClient] = useState('')
  const [selectedClientId, setSelectedClientId] = useState(null)
  const [activeTab, setActiveTab] = useState('coverage') // 'coverage' | 'telemetry'
  const [selectedNode, setSelectedNode] = useState('all')
  const [timeRange, setTimeRange] = useState('1h')
  const [searchQuery, setSearchQuery] = useState('')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastRefreshed, setLastRefreshed] = useState(new Date().toLocaleTimeString())
  const [fleetNodes, setFleetNodes] = useState([])
  const [rawLogs, setRawLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [copiedCmd, setCopiedCmd] = useState(false)
  const [copiedBulkSsm, setCopiedBulkSsm] = useState(false)
  const [copiedScriptId, setCopiedScriptId] = useState(null)
  const [pulseManagerHost, setPulseManagerHost] = useState('')

  // Fleet Coverage Matrix state
  const [coverageData, setCoverageData] = useState(null)
  const [coverageLoading, setCoverageLoading] = useState(false)
  const [coverageFilter, setCoverageFilter] = useState('all')
  const [coverageSearch, setCoverageSearch] = useState('')

  // 1. Fetch available client accounts and Pulse Manager status dynamically
  useEffect(() => {
    getPulseStatus().then(st => {
      if (st?.manager_host) setPulseManagerHost(st.manager_host)
    }).catch(() => {})

    getClients().then(res => {
      const cls = res?.clients || []
      setClientList(cls)
      if (cls.length > 0) {
        setSelectedClient(cls[0].account_id)
        setSelectedClientId(cls[0].id)
        fetchPulseData(cls[0].account_id)
        fetchCoverageData(cls[0].id)
      } else {
        setSelectedClient('')
        setSelectedClientId(null)
      }
    }).catch(() => {
      setClientList([])
    })
  }, [])

  // 2. Fetch Fleet Coverage Matrix (EC2 Describe + SSM + Pulse cross-reference)
  const fetchCoverageData = async (cid) => {
    const targetId = cid !== undefined ? cid : (selectedClientId || clientList.find(c => c.account_id === selectedClient)?.id)
    if (!targetId) {
      setCoverageData(null)
      return
    }
    setCoverageLoading(true)
    try {
      const res = await getFleetCoverage(targetId)
      setCoverageData(res)
    } catch (err) {
      console.warn('Coverage fetch notice:', err)
      setCoverageData(null)
    } finally {
      setCoverageLoading(false)
    }
  }

  // 3. Fetch real fleet nodes and anomalies for the selected client account
  const fetchPulseData = async (accId) => {
    const targetAccount = accId || selectedClient
    if (!targetAccount) {
      setFleetNodes([])
      setRawLogs([])
      setLastRefreshed(new Date().toLocaleTimeString())
      return
    }
    setLoading(true)
    try {
      const fleetRes = await getPulseFleet(targetAccount)
      if (fleetRes?.agents && Array.isArray(fleetRes.agents)) {
        setFleetNodes(fleetRes.agents.map(ag => ({
          id: ag.agent_id,
          name: ag.server_name || ag.hostname,
          hostname: ag.hostname,
          instance_id: ag.instance_id,
          ip: ag.ip_address || 'dynamic',
          role: ag.os_name || 'CloudPulse Node',
          cpu: typeof ag.cpu_pct === 'number' ? ag.cpu_pct : null,
          mem: typeof ag.mem_pct === 'number' ? ag.mem_pct : null,
          disk: typeof ag.disk_pct === 'number' ? ag.disk_pct : null,
          cpu_cores: ag.cpu_cores,
          ram_total_mb: ag.ram_total_mb,
          active_services: ag.active_services,
          hosted_domains: ag.hosted_domains || '',
          listening_ports: ag.listening_ports,
          recent_logs: ag.recent_logs,
          status: ag.status, // 'active', 'disconnected', 'never_connected'
          last_seen: ag.last_seen || 'N/A',
          version: ag.version || 'CloudPulse v4.8.2'
        })))
      } else {
        setFleetNodes([])
      }

      const logsRes = await getPulseAnomalies(selectedClient)
      if (logsRes?.anomalies && Array.isArray(logsRes.anomalies)) {
        setRawLogs(logsRes.anomalies.map((anom, idx) => ({
          id: idx + 1,
          time: anom.timestamp ? new Date(anom.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString(),
          host: anom.hostname || 'host',
          service: anom.service || 'cloudpulse',
          level: (anom.rule_level || 0) >= 8 ? 'CRITICAL' : (anom.rule_level || 0) >= 5 ? 'WARNING' : 'INFO',
          msg: anom.rule_description || anom.full_log || 'Application event recorded'
        })))
      } else {
        setRawLogs([])
      }
    } catch (err) {
      console.warn('Pulse API fetch notice:', err)
      setFleetNodes([])
      setRawLogs([])
    } finally {
      setLoading(false)
      setLastRefreshed(new Date().toLocaleTimeString())
    }
  }

  useEffect(() => {
    fetchPulseData()
    fetchCoverageData()
    setSelectedNode('all')
  }, [selectedClient, selectedClientId])

  const totalCount = fleetNodes.length
  const activeNodes = fleetNodes.filter(n => n.status === 'active')
  const activeCount = activeNodes.length

  const avgCpu = useMemo(() => {
    const withCpu = activeNodes.filter(n => typeof n.cpu === 'number')
    if (withCpu.length === 0) return null
    return Math.round(withCpu.reduce((acc, n) => acc + n.cpu, 0) / withCpu.length * 10) / 10
  }, [activeNodes])

  const avgMem = useMemo(() => {
    const withMem = activeNodes.filter(n => typeof n.mem === 'number')
    if (withMem.length === 0) return null
    return Math.round(withMem.reduce((acc, n) => acc + n.mem, 0) / withMem.length * 10) / 10
  }, [activeNodes])

  const peakDisk = useMemo(() => {
    const withDisk = activeNodes.filter(n => typeof n.disk === 'number')
    if (withDisk.length === 0) return null
    return Math.max(...withDisk.map(n => n.disk))
  }, [activeNodes])

  const filteredLogs = useMemo(() => {
    return rawLogs.filter(log => {
      const matchSearch = !searchQuery || log.msg.toLowerCase().includes(searchQuery.toLowerCase()) || log.host.toLowerCase().includes(searchQuery.toLowerCase())
      const matchSeverity = severityFilter === 'all' || log.level === severityFilter
      return matchSearch && matchSeverity
    })
  }, [rawLogs, searchQuery, severityFilter])

  const installCommand = selectedClient
    ? `curl -sSL ${window.location.origin}/install-pulse.sh | sudo bash -s -- --manager=${pulseManagerHost || 'PULSE_MANAGER_HOST'} --account=${selectedClient} --group=pulse_acc_${selectedClient} --studio=${window.location.origin}`
    : ''

  const handleCopyInstall = () => {
    if (!installCommand) return
    navigator.clipboard.writeText(installCommand)
    setCopiedCmd(true)
    setTimeout(() => setCopiedCmd(false), 3000)
  }

  return (
    <div className="animate-in" style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Top Header & Client Account Selector */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity size={24} style={{ color: 'var(--brand-blue)' }} />
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
              CloudIQ Pulse — Telemetry &amp; Log Explorer
            </h2>
          </div>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-600)', fontSize: 13 }}>
            Real-time measured telemetry and application event logs across connected hosts. Zero synthetic data.
          </p>
        </div>

        {/* Global Controls */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={selectedClientId || ''}
            onChange={(e) => {
              const cid = parseInt(e.target.value, 10)
              setSelectedClientId(cid)
              const matched = clientList.find(c => c.id === cid)
              if (matched) setSelectedClient(matched.account_id)
            }}
            className="input-select"
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              fontSize: 13,
              fontWeight: 600,
              background: '#FFFFFF',
              boxShadow: 'var(--shadow-solid-sm)',
              cursor: 'pointer'
            }}
          >
            {clientList.length === 0 ? (
              <option value="">No clients configured</option>
            ) : (
              clientList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.client_name} ({c.account_id})
                </option>
              ))
            )}
          </select>

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="input-select"
            style={{
              padding: '6px 10px',
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              fontSize: 13,
              fontWeight: 500,
              background: '#FFFFFF',
              boxShadow: 'var(--shadow-solid-sm)'
            }}
          >
            <option value="1h">Last 1 Hour (1m)</option>
            <option value="6h">Last 6 Hours (5m)</option>
            <option value="24h">Last 24 Hours (15m)</option>
            <option value="7d">Last 7 Days (1h)</option>
          </select>

          <button
            type="button"
            onClick={() => {
              fetchPulseData()
              fetchCoverageData()
            }}
            className="btn btn-secondary btn-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              fontWeight: 700
            }}
          >
            <RefreshCw size={13} className={(loading || coverageLoading) ? 'spin' : ''} />
            <span>Sync ({lastRefreshed})</span>
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1.5px solid var(--border-ink)', paddingBottom: 10 }}>
        <button
          type="button"
          onClick={() => setActiveTab('coverage')}
          className={`btn ${activeTab === 'coverage' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '8px 18px', fontSize: 13, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)', fontWeight: 700 }}
        >
          <Server size={14} style={{ marginRight: 6 }} />
          Fleet Coverage &amp; Bulk Deployment ({coverageData?.total_instances ?? '...'})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('telemetry')}
          className={`btn ${activeTab === 'telemetry' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '8px 18px', fontSize: 13, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)', fontWeight: 700 }}
        >
          <Activity size={14} style={{ marginRight: 6 }} />
          Live Metrics &amp; Host Logs ({totalCount})
        </button>
      </div>

      {/* ── TAB 1: FLEET COVERAGE MATRIX & BULK SSM DEPLOYMENT ── */}
      {activeTab === 'coverage' && (
        <div style={{ marginBottom: 30 }}>
          {/* Coverage KPI Quick Bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
            {/* Total Fleet Instances */}
            <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total AWS EC2 Fleet
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--ink-900)', marginTop: 4 }}>
                {coverageData?.total_instances ?? 0}
                <span style={{ fontSize: 12, color: 'var(--ink-500)', fontWeight: 500, marginLeft: 6 }}>servers discovered</span>
              </div>
            </div>

            {/* Pulse Active Nodes */}
            <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', borderLeft: '6px solid #10B981', boxShadow: 'var(--shadow-solid-sm)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#047857', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <CheckCircle size={14} /> Pulse Active &amp; Streaming
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: '#047857', marginTop: 4 }}>
                {coverageData?.pulse_active_count ?? 0}
                <span style={{ fontSize: 12, color: 'var(--ink-500)', fontWeight: 500, marginLeft: 6 }}>nodes enrolled</span>
              </div>
            </div>

            {/* Pending Pulse Deployment */}
            <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', borderLeft: '6px solid var(--brand-coral)', boxShadow: 'var(--shadow-solid-sm)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--brand-coral)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Clock size={14} /> Pending Pulse Deployment
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--brand-coral)', marginTop: 4 }}>
                {coverageData?.pulse_pending_count ?? 0}
                <span style={{ fontSize: 12, color: 'var(--ink-500)', fontWeight: 500, marginLeft: 6 }}>awaiting agent</span>
              </div>
            </div>

            {/* SSM Ready Nodes */}
            <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', borderLeft: '6px solid var(--brand-blue)', boxShadow: 'var(--shadow-solid-sm)' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--brand-blue)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Zap size={14} /> SSM Managed (1-Click Ready)
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--brand-blue)', marginTop: 4 }}>
                {coverageData?.ssm_managed_count ?? 0}
                <span style={{ fontSize: 12, color: 'var(--ink-500)', fontWeight: 500, marginLeft: 6 }}>instances eligible</span>
              </div>
            </div>
          </div>

          {/* Bulk Systems Manager (SSM) Deployment Banner */}
          <div style={{
            background: 'var(--brand-blue-subtle)',
            border: '2px solid var(--border-ink)',
            borderRadius: 12,
            padding: '18px 22px',
            marginBottom: 20,
            boxShadow: 'var(--shadow-solid-sm)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Terminal size={18} style={{ color: 'var(--brand-blue)' }} />
                  <h3 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                    Scalable Bulk Fleet Deployment (AWS Systems Manager)
                  </h3>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--ink-700)', maxWidth: 840, lineHeight: 1.5 }}>
                  Deploy the CloudIQ Pulse Agent simultaneously across all <strong>{coverageData?.total_instances || 50}+ servers</strong> in account <code>{selectedClient}</code> without SSH keys or Bastion logins. Already-enrolled nodes (e.g. <code>ip-172-31-5-75</code>, <code>ip-172-31-7-55</code>) are safely idempotent and will not be interrupted.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(coverageData?.bulk_ssm_command || '')
                  setCopiedBulkSsm(true)
                  setTimeout(() => setCopiedBulkSsm(false), 3000)
                }}
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}
              >
                {copiedBulkSsm ? <Check size={14} /> : <Copy size={14} />}
                {copiedBulkSsm ? 'SSM Command Copied' : 'Copy AWS SSM Run Command'}
              </button>
            </div>

            <div style={{
              marginTop: 12,
              background: '#FFFFFF',
              border: '1.5px solid var(--border-ink)',
              borderRadius: 8,
              padding: '10px 14px',
              fontFamily: 'var(--font-mono)',
              fontSize: 11.5,
              color: 'var(--ink-800)',
              overflowX: 'auto',
              whiteSpace: 'pre'
            }}>
              {coverageData?.bulk_ssm_command || 'Loading AWS SSM command...'}
            </div>
          </div>

          {/* Fleet Coverage Matrix Table */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 12,
            border: '2px solid var(--border-ink)',
            boxShadow: 'var(--shadow-solid-md)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '2px solid var(--border-ink)',
              background: 'var(--canvas-subtle)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
                  Fleet Server Coverage Matrix ({coverageData?.instances?.length || 0} Instances)
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-600)' }}>
                  Comprehensive map of servers, resolving AWS Name tags, active Pulse status, and 1-click install snippets.
                </p>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Filter by name, instance ID, IP..."
                  value={coverageSearch}
                  onChange={(e) => setCoverageSearch(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1.5px solid var(--border-ink)',
                    fontSize: 12,
                    fontFamily: 'var(--font-mono)',
                    width: 220
                  }}
                />
                <select
                  value={coverageFilter}
                  onChange={(e) => setCoverageFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1.5px solid var(--border-ink)',
                    fontSize: 12,
                    fontWeight: 700
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active Only</option>
                  <option value="pending">Pending Only</option>
                  <option value="ssm">SSM Ready Only</option>
                </select>
              </div>
            </div>

            {coverageLoading ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-500)', fontSize: 13 }}>
                Scanning EC2 inventory and cross-referencing Pulse agent heartbeats...
              </div>
            ) : !coverageData?.instances || coverageData.instances.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-500)' }}>
                <Server size={32} style={{ color: 'var(--ink-400)', margin: '0 auto 10px', display: 'block' }} />
                <div style={{ fontWeight: 800, color: 'var(--ink-900)', fontSize: 14 }}>No Instances Discovered</div>
                <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4 }}>
                  Ensure the client account has AWS read access configured or run discovery in Client Settings.
                </div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: 'var(--canvas-subtle)', borderBottom: '2px solid var(--border-ink)' }}>
                      <th style={{ padding: '10px 16px', fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>Server Name (AWS Tag)</th>
                      <th style={{ padding: '10px 16px', fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>Instance ID</th>
                      <th style={{ padding: '10px 16px', fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>Private IP</th>
                      <th style={{ padding: '10px 16px', fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>SSM Status</th>
                      <th style={{ padding: '10px 16px', fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>Pulse Status</th>
                      <th style={{ padding: '10px 16px', fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>Discovered Services</th>
                      <th style={{ padding: '10px 16px', fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11, textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coverageData.instances
                      .filter(inst => {
                        if (coverageFilter === 'active' && inst.pulse_status !== 'ACTIVE') return false
                        if (coverageFilter === 'pending' && inst.pulse_status !== 'PENDING') return false
                        if (coverageFilter === 'ssm' && inst.ssm_status !== 'ONLINE') return false
                        if (!coverageSearch) return true
                        const q = coverageSearch.toLowerCase()
                        return (
                          (inst.server_name && inst.server_name.toLowerCase().includes(q)) ||
                          (inst.instance_id && inst.instance_id.toLowerCase().includes(q)) ||
                          (inst.private_ip && inst.private_ip.toLowerCase().includes(q))
                        )
                      })
                      .map(inst => {
                        const isPulseActive = inst.pulse_status === 'ACTIVE'
                        const isSsmReady = inst.ssm_status === 'ONLINE' || inst.ssm_status === 'MANAGED'
                        const isCopied = copiedScriptId === inst.instance_id

                        return (
                          <tr key={inst.instance_id} style={{ borderBottom: '1px solid var(--border-subtle)', background: isPulseActive ? '#F0FDF4' : '#FFFFFF' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ fontWeight: 800, color: 'var(--ink-900)' }}>
                                {inst.server_name}
                              </div>
                              {inst.hosted_domains && (
                                <div style={{ fontSize: 11, color: 'var(--brand-blue)', fontWeight: 700, fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                                  {inst.hosted_domains}
                                </div>
                              )}
                              <div style={{ fontSize: 11, color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
                                {inst.instance_type} &middot; {inst.state}
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-700)' }}>
                              {inst.instance_id}
                            </td>
                            <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-700)' }}>
                              {inst.private_ip || 'N/A'}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '2px 8px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 700,
                                background: isSsmReady ? '#EFF6FF' : 'var(--canvas-subtle)',
                                color: isSsmReady ? 'var(--brand-blue)' : 'var(--ink-600)',
                                border: `1.5px solid ${isSsmReady ? '#93C5FD' : 'var(--border-subtle)'}`
                              }}>
                                {isSsmReady ? 'SSM Online' : 'Not Configured'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {isPulseActive ? (
                                <div>
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    background: '#DCFCE7',
                                    color: '#047857',
                                    border: '1.5px solid #86EFAC'
                                  }}>
                                    <CheckCircle size={12} /> PULSE ACTIVE
                                  </span>
                                  {inst.cpu_pct !== null && inst.cpu_pct !== undefined && (
                                    <div style={{ fontSize: 11, color: 'var(--ink-600)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                                      CPU: {inst.cpu_pct}% | RAM: {inst.mem_pct}% | Disk: {inst.disk_pct}%
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '2px 8px',
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  background: 'var(--brand-yellow-bg)',
                                  color: '#B45309',
                                  border: '1.5px solid #FCD34D'
                                }}>
                                  <Clock size={12} /> PENDING
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {inst.active_services ? (
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {inst.active_services.split(',').filter(Boolean).map(s => (
                                    <span key={s} style={{
                                      fontSize: 10,
                                      padding: '1px 6px',
                                      borderRadius: 4,
                                      background: 'var(--canvas-subtle)',
                                      border: '1px solid var(--border-subtle)',
                                      fontFamily: 'var(--font-mono)',
                                      color: 'var(--ink-800)',
                                      fontWeight: 600
                                    }}>
                                      {s.trim()}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ fontSize: 11, color: 'var(--ink-400)', fontStyle: 'italic' }}>
                                  Auto-detects upon probe
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              {isPulseActive ? (
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#047857', fontFamily: 'var(--font-mono)' }}>
                                  Telemetry Streaming
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(inst.install_command)
                                    setCopiedScriptId(inst.instance_id)
                                    setTimeout(() => setCopiedScriptId(null), 2500)
                                  }}
                                  className="btn btn-secondary btn-sm"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    fontSize: 11,
                                    fontFamily: 'var(--font-mono)',
                                    fontWeight: 700,
                                    border: '1.5px solid var(--border-ink)'
                                  }}
                                  title="Copy 1-line installer for this specific server"
                                >
                                  {isCopied ? <Check size={12} style={{ color: '#047857' }} /> : <Copy size={12} />}
                                  {isCopied ? 'Command Copied' : 'Copy Script'}
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 2: LIVE METRICS & HOST LOGS ── */}
      {activeTab === 'telemetry' && (
        <div>
      {/* Fleet KPI Quick Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-600)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' }}>
            <Server size={14} style={{ color: 'var(--brand-blue)' }} /> Active Fleet Nodes
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-900)', marginTop: 6 }}>
            {totalCount > 0 ? (
              <>
                {activeCount} / {totalCount}{' '}
                <span style={{ fontSize: 13, color: activeCount > 0 ? '#047857' : 'var(--brand-coral)', fontWeight: 600 }}>
                  {activeCount === totalCount ? '100% Healthy' : `${Math.round(activeCount * 100 / totalCount)}% Online`}
                </span>
              </>
            ) : (
              <span style={{ color: 'var(--ink-400)', fontSize: 18 }}>0 / 0 <span style={{ fontSize: 12 }}>(None deployed)</span></span>
            )}
          </div>
        </div>

        <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-600)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' }}>
            <Cpu size={14} style={{ color: 'var(--brand-blue)' }} /> Fleet Average CPU
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-900)', marginTop: 6 }}>
            {avgCpu !== null ? `${avgCpu}%` : <span style={{ color: 'var(--ink-400)', fontSize: 18 }}>-- <span style={{ fontSize: 12 }}>(Awaiting stream)</span></span>}
          </div>
        </div>

        <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-600)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' }}>
            <Database size={14} style={{ color: 'var(--brand-blue)' }} /> Fleet Average Memory
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-900)', marginTop: 6 }}>
            {avgMem !== null ? `${avgMem}%` : <span style={{ color: 'var(--ink-400)', fontSize: 18 }}>-- <span style={{ fontSize: 12 }}>(Awaiting stream)</span></span>}
          </div>
        </div>

        <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-600)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' }}>
            <HardDrive size={14} style={{ color: 'var(--brand-blue)' }} /> Peak Storage Usage
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-900)', marginTop: 6 }}>
            {peakDisk !== null ? `${peakDisk}%` : <span style={{ color: 'var(--ink-400)', fontSize: 18 }}>-- <span style={{ fontSize: 12 }}>(Awaiting stream)</span></span>}
          </div>
        </div>
      </div>

      {/* Main Dual Panel: Server Nodes & Telemetry Stream */}
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, marginBottom: 24 }}>
        {/* Left Server Node Selector */}
        <div style={{ background: '#FFFFFF', padding: 18, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
            <Layers size={16} style={{ color: 'var(--brand-blue)' }} />
            <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
              Server Nodes ({fleetNodes.length})
            </h3>
          </div>

          {fleetNodes.length === 0 ? (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--ink-500)' }}>
              <Server size={32} style={{ color: 'var(--ink-400)', margin: '0 auto 10px', display: 'block' }} />
              <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--ink-900)', marginBottom: 4 }}>
                No Pulse Agents Connected
              </div>
              <p style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5, margin: '0 0 16px' }}>
                Account <strong>{selectedClient || 'selected'}</strong> has no agents reporting yet.
              </p>
              <button
                type="button"
                onClick={handleCopyInstall}
                className="btn btn-secondary btn-sm"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  borderRadius: 6, cursor: 'pointer', width: '100%', justifyContent: 'center',
                  fontWeight: 700, border: '1.5px solid var(--border-ink)'
                }}
              >
                {copiedCmd ? <Check size={13} style={{ color: '#047857' }} /> : <Copy size={13} />}
                {copiedCmd ? 'Command Copied' : 'Copy 1-Line Installer'}
              </button>
            </div>
          ) : (
            <>
              <div
                onClick={() => setSelectedNode('all')}
                style={{
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 8,
                  background: selectedNode === 'all' ? 'var(--brand-blue-subtle)' : 'var(--canvas-bg)',
                  border: selectedNode === 'all' ? '2px solid var(--brand-blue)' : '1px solid var(--border-subtle)',
                  boxShadow: selectedNode === 'all' ? 'var(--shadow-solid-blue)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13, color: selectedNode === 'all' ? 'var(--brand-blue)' : 'var(--ink-900)' }}>
                  All Nodes (Fleet View)
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>Combined client metrics</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {fleetNodes.map(node => (
                  <div
                    key={node.id}
                    onClick={() => setSelectedNode(node.id)}
                    style={{
                      padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      background: selectedNode === node.id ? 'var(--brand-blue-subtle)' : '#FFFFFF',
                      border: selectedNode === node.id ? '2px solid var(--brand-blue)' : '1px solid var(--border-subtle)',
                      boxShadow: selectedNode === node.id ? 'var(--shadow-solid-blue)' : 'none',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--ink-900)' }}>{node.name}</span>
                      <span className={`badge ${node.status === 'active' ? 'badge-ok' : 'badge-warning'}`} style={{ fontSize: 10 }}>
                        {node.status === 'active' ? 'Active' : node.status}
                      </span>
                    </div>
                    {node.hosted_domains && (
                      <div style={{ fontSize: 11, color: 'var(--brand-blue)', fontWeight: 700, fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                        {node.hosted_domains.split(',')[0]}
                      </div>
                    )}
                    <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 3 }}>
                      {node.ip} • {node.role}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-400)', marginTop: 2 }}>
                      Seen: {node.last_seen}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Center / Right: Telemetry Charts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Chart 1: CPU & Memory Curves */}
          <div style={{ background: '#FFFFFF', padding: 20, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                  CPU &amp; Memory Utilization (%) — 1-Minute Resolution
                </h3>
                <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>
                  Host: {selectedNode === 'all' ? 'Fleet Average' : fleetNodes.find(n => n.id === selectedNode)?.name}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, fontWeight: 700 }}>
                <span style={{ color: 'var(--brand-blue)' }}>● CPU %</span>
                <span style={{ color: '#7C3AED' }}>● Memory %</span>
              </div>
            </div>

            {activeCount === 0 ? (
              <div style={{ height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1.5px dashed var(--border-subtle)', borderRadius: 8, background: 'var(--canvas-bg)', color: 'var(--ink-600)' }}>
                <Activity size={28} style={{ color: 'var(--ink-400)', marginBottom: 6 }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-900)' }}>Awaiting Telemetry Stream</div>
                <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>
                  Install the CloudIQ Pulse Agent on your servers to begin real-time 1-minute graphing.
                </div>
              </div>
            ) : (
              <div style={{ height: 160, width: '100%', display: 'flex', alignItems: 'flex-end', gap: 6, borderBottom: '1.5px solid var(--border-ink)', paddingBottom: 8 }}>
                {[...Array(20)].map((_, idx) => (
                  <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', gap: 2 }}>
                    <div style={{ height: `${avgCpu || 20}%`, background: 'var(--brand-blue)', borderRadius: 2, opacity: 0.85 }} />
                    <div style={{ height: `${(avgMem || 40) * 0.5}%`, background: '#8B5CF6', borderRadius: 2, opacity: 0.65 }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Chart 2: Disk & Storage Trends */}
          <div style={{ background: '#FFFFFF', padding: 20, borderRadius: 12, border: '2px solid var(--border-ink)', boxShadow: 'var(--shadow-solid-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                  Disk Space &amp; Storage Consumption (%)
                </h3>
                <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>Capacity tracker</span>
              </div>
              <span style={{ color: '#D97706', fontSize: 12, fontWeight: 700 }}>● Disk %</span>
            </div>

            {activeCount === 0 ? (
              <div style={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px dashed var(--border-subtle)', borderRadius: 8, background: 'var(--canvas-bg)', color: 'var(--ink-500)', fontSize: 12 }}>
                No storage metrics reported for this client account.
              </div>
            ) : (
              <div style={{ height: 90, width: '100%', display: 'flex', alignItems: 'flex-end', gap: 6, borderBottom: '1.5px solid var(--border-ink)', paddingBottom: 8 }}>
                {[...Array(20)].map((_, idx) => (
                  <div key={idx} style={{ flex: 1, height: `${peakDisk || 35}%`, background: '#F59E0B', borderRadius: 2, opacity: 0.8 }} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Application & Telemetry Log Stream */}
      <div style={{ background: '#FFFFFF', borderRadius: 12, border: '2px solid var(--border-ink)', padding: 22, boxShadow: 'var(--shadow-solid-md)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Terminal size={18} style={{ color: 'var(--brand-blue)' }} />
              <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' }}>
                Live Application &amp; Telemetry Log Stream
              </h3>
            </div>
            <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>Real-time measured events tailing CloudIQ Pulse agents</span>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--ink-400)' }} />
              <input
                type="text"
                placeholder="Search logs, keywords, services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-text font-mono"
                style={{ padding: '6px 12px 6px 30px', borderRadius: 6, border: '1.5px solid var(--border-ink)', fontSize: 12, width: 240, boxShadow: 'var(--shadow-solid-sm)' }}
              />
            </div>

            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="input-select"
              style={{ padding: '6px 10px', borderRadius: 6, border: '1.5px solid var(--border-ink)', fontSize: 12, fontWeight: 600, boxShadow: 'var(--shadow-solid-sm)' }}
            >
              <option value="all">All Severities</option>
              <option value="INFO">INFO Only</option>
              <option value="WARNING">WARNING Only</option>
              <option value="CRITICAL">CRITICAL Only</option>
            </select>
          </div>
        </div>

        {/* Log Stream Table */}
        <div style={{ overflowX: 'auto', border: '1.5px solid var(--border-ink)', borderRadius: 8, boxShadow: 'var(--shadow-solid-sm)' }}>
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: 'var(--canvas-bg)', borderBottom: '1.5px solid var(--border-ink)', color: 'var(--ink-800)', fontWeight: 800 }}>
                <th style={{ padding: '10px 14px', width: 110 }}>Time</th>
                <th style={{ padding: '10px 14px', width: 140 }}>Host</th>
                <th style={{ padding: '10px 14px', width: 110 }}>Service</th>
                <th style={{ padding: '10px 14px', width: 100 }}>Severity</th>
                <th style={{ padding: '10px 14px' }}>Log Message / Exception Trace</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '36px 14px', textAlign: 'center', color: 'var(--ink-500)' }}>
                    <Terminal size={26} style={{ color: 'var(--ink-400)', margin: '0 auto 8px', display: 'block' }} />
                    <div style={{ fontWeight: 800, color: 'var(--ink-900)', fontSize: 13 }}>No Event Logs Recorded</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>
                      {fleetNodes.length === 0
                        ? `Deploy a Pulse agent for account ${selectedClient || ''} to begin receiving logs.`
                        : 'No critical errors or anomalies detected for this account.'}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLogs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-600)', fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{log.time}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--ink-900)' }}>{log.host}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span className="badge badge-neutral" style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                        {log.service}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span className={`badge ${log.level === 'CRITICAL' ? 'badge-alarm' : (log.level === 'WARNING' ? 'badge-warning' : 'badge-ok')}`} style={{ fontSize: 10.5, fontWeight: 800 }}>
                        {log.level}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-800)', fontFamily: 'var(--font-mono)', fontSize: 11.5, wordBreak: 'break-all' }}>
                      {log.msg}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      )}
    </div>
  )
}

